import { Injectable } from '@angular/core';
import {
  AccountStatus,
  AutoCloseStepTasksPolicy,
  CloseStepTasksPolicy,
  PlannerSolverType,
  PlanningViewMode,
  resolve,
  RoutingProvider,
  StartNextStepPolicy,
  StepAppearanceField,
  TenantLanguage,
} from '../../api/gqty';
import { formatApiError, touchMutationErrors } from '../../api';
import { forwardPagination } from '../../utils';
import { readDeleteAccountError, throwIfDeleteBlocked } from './account-actions';

export interface Worker {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  name: string;
  status: AccountStatus;
  phone: string | null;
  resourceId: string | null;
  resourceName: string | null;
  createdAt?: Date;
}

export interface Manager {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  name: string;
  status: AccountStatus;
  createdAt?: Date;
}

export interface Administrator {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  name: string;
  status: AccountStatus;
  createdAt?: Date;
}

export interface ApiKey {
  id: string;
  name: string;
  key?: string; // Only present when first created
}

export interface Webhook {
  id: string;
  name: string;
  url: string;
  events: string[];
  isActive: boolean;
  secret?: string;
  createdAt?: Date;
}

export interface WorkPackageType {
  id: string;
  name: string;
  createdAt?: Date;
}

export interface ResourceFeature {
  id: string;
  name: string;
  createdAt?: Date;
}

export interface WorkPackageTemplate {
  id: string;
  code: string | null;
  name: string | null;
  notes: string | null;
  createdAt?: Date;
}

export interface GeneralSettings {
  tenantName: string;
  tenantCode: string;
  timezone: string;
  defaultCurrency: string;
  language: TenantLanguage;
}

/** A license assigned to this tenant by Everplan (read-only). */
export interface License {
  id: string;
  type: string;
  qt: number;
  validFrom: string;
  validTo: string;
  isTrial: boolean;
  singlePrice: number;
  isActive: boolean;
  seatsInUse: number;
}

/** The license module types that enforce a seat cap. */
export type SeatLicenseType = 'EVERPLAN_PLANNING' | 'EVERPLAN_LIVE';

/** Seat usage for one module, aggregated across that module's active licenses. */
export interface SeatUsage {
  type: SeatLicenseType;
  used: number;
  total: number;
  isFull: boolean;
}

export interface PlanningSettingsForm {
  defaultSolverType: PlannerSolverType;
  defaultPlanningHorizonDays: number;
  penaltyAwarePriority: boolean;
  /** Overtime seeded into planning resources created without their own. */
  defaultMaxOvertimeMinutes: number;
  defaultShiftEndBehaviour: 'RETURN_TO_DEPOT' | 'STAY_IN_PLACE';
  defaultTimeLimitSec: number;
  defaultPlanningViewMode: PlanningViewMode;
  defaultRoutingProvider: RoutingProvider;
  defaultCloseStopTasksPolicy: string;
  defaultStartNextStopsPolicy: string;
  autoCloseStopTasksPolicy: string;
  startTasksOnStepStarted: boolean;
  inProgressWorkPackageOnTaskStart: boolean;
  inProgressWorkShiftOnStepStart: boolean;
  autoStartLaneDelayedStepOnWorkShiftStart: boolean;
  completeWorkPackageOnAllTaskComplete: boolean;
  completeWorkShiftOnAllStepComplete: boolean;
  autoScheduleEnabled: boolean;
  allowOverlappingTasks: boolean;
  workingHoursStart: string;
  workingHoursEnd: string;
  stepTitle: StepAppearanceField;
  stepSubtitle: StepAppearanceField;
  stepShowCode: boolean;
  stepShowTaskBadge: boolean;
  requireApplyConfirmation: boolean;
  workerPushLeadMinutes: number;
  averageTravelSpeedKmh: number;
  /**
   * A new Google Directions key to store. The stored one is never readable, so
   * this always starts empty and is only sent when the user types something —
   * leaving it blank keeps whatever is saved.
   */
  googleMapsApiKey: string;
  /** Clears the stored Google key on save, whatever `googleMapsApiKey` holds. */
  clearGoogleMapsApiKey: boolean;
}

/**
 * Reads a GraphQL union error member's message. MUST be called inside a gqty
 * resolver so the `$on.*.message` accesses are recorded as part of the selection.
 */
function settingsErrorMessage(err: { __typename?: string; $on?: any } | undefined | null): string | null {
  if (!err) return null;
  const on = err.$on;
  return (
    on?.ConflictError?.message ??
    on?.BadRequestError?.message ??
    on?.NotFoundError?.message ??
    on?.ForbiddenError?.message ??
    on?.UnauthorizedError?.message ??
    err.__typename ??
    'unknown error'
  );
}

function localTimeToInput(value: string | null | undefined): string {
  if (!value) return '';
  const part = value.split(':');
  if (part.length < 2) return value;
  return `${part[0].padStart(2, '0')}:${part[1].padStart(2, '0')}`;
}

function inputToLocalTime(value: string): string | null {
  if (!value) return null;
  return value.length === 5 ? `${value}:00` : value;
}

@Injectable({ providedIn: 'root' })
export class SettingsService {
  readonly closeStepTasksPolicyOptions = [
    { value: 'LEAVE_AS_IS', labelKey: 'settings.planning.executor.policies.closeStep.leaveAsIs' },
    { value: 'COMPLETE_DOING_ONLY', labelKey: 'settings.planning.executor.policies.closeStep.completeDoingOnly' },
    { value: 'COMPLETE_ALL_OPEN', labelKey: 'settings.planning.executor.policies.closeStep.completeAllOpen' },
    { value: 'SKIP_ALL_OPEN', labelKey: 'settings.planning.executor.policies.closeStep.skipAllOpen' },
  ];

  readonly startNextStepPolicyOptions = [
    { value: 'NEVER', labelKey: 'settings.planning.executor.policies.startNext.never' },
    { value: 'IF_NEXT_READY', labelKey: 'settings.planning.executor.policies.startNext.ifNextReady' },
    { value: 'ALWAYS', labelKey: 'settings.planning.executor.policies.startNext.always' },
    { value: 'ADVANCE_ONE_ONLY', labelKey: 'settings.planning.executor.policies.startNext.advanceOneOnly' },
  ];

  readonly autoCloseStepTasksPolicyOptions = [
    { value: 'COMPLETE_LINKED_TASKS', labelKey: 'settings.planning.executor.policies.autoClose.completeLinked' },
    { value: 'LEAVE_AS_IS', labelKey: 'settings.planning.executor.policies.autoClose.leaveAsIs' },
  ];
  // ============================================
  // Mock Webhook Storage (for demonstration)
  // ============================================
  private mockWebhooks: Webhook[] = [
    {
      id: '1',
      name: 'Work Package Created',
      url: 'https://example.com/webhooks/workpackage-created',
      events: ['workPackage.created'],
      isActive: true,
      createdAt: new Date(),
    },
    {
      id: '2',
      name: 'Task Notifications',
      url: 'https://api.example.com/notify',
      events: ['task.created', 'task.updated', 'task.completed'],
      isActive: false,
      createdAt: new Date(),
    },
  ];
  private webhookIdCounter = 3;

  // ============================================
  // CRUD Entities (Workers, Managers, etc.)
  // ============================================

  /**
   * Get a single worker by ID
   */
  async getWorker(id: string): Promise<Worker | null> {
    return resolve(
      ({ query }) => {
        const worker = query.worker({ id });
        if (!worker) return null;

        return {
          id: worker.id,
          email: worker.email ?? '',
          firstName: worker.firstName ?? '',
          lastName: worker.lastName ?? '',
          name: worker.name ?? '',
          status: worker.status,
          phone: worker.workerResource?.profile?.phoneNumber ?? null,
          resourceId: worker.workerResourceId ?? null,
          resourceName: worker.workerResource?.code ?? null,
          createdAt: worker.createdAt,
        };
      },
      { cachePolicy: 'no-cache' }
    );
  }

  /**
   * Get workers with cursor-based pagination
   */
  async getWorkersCursor(params: { cursor: string | null; search?: string | null }) {
    const { cursor, search } = params;
    const where: any = {};

    if (search) {
      where.search = { contains: search };
    }

    return resolve(
      ({ query }) => {
        const conn = query.workers({
          ...forwardPagination(25, cursor),
          where: Object.keys(where).length > 0 ? where : undefined,
        });

        if (!conn) return { nodes: [], pageInfo: { endCursor: null, hasNextPage: false } };

        const nodes = conn.nodes?.map((node) => ({
          id: node.id,
          email: node.email ?? '',
          firstName: node.firstName ?? '',
          lastName: node.lastName ?? '',
          name: node.name ?? '',
          status: node.status,
          phone: node.workerResource?.profile?.phoneNumber ?? null,
          resourceId: node.workerResourceId ?? null,
          resourceName: node.workerResource?.code ?? null,
          createdAt: node.createdAt,
        })) ?? [];

        return {
          nodes,
          pageInfo: {
            endCursor: conn.pageInfo?.endCursor,
            hasNextPage: conn.pageInfo?.hasNextPage,
          },
        };
      },
      { cachePolicy: 'no-cache' }
    );
  }

  /**
   * Create or update a worker
   */
  async upsertWorker(input: { id?: string; email: string; firstName: string; lastName: string; phone: string | null; resourceId: string }) {
    if (input.id) {
      return resolve(
        ({ mutation }) => {
          const result = mutation.updateAccount({
            input: {
              id: input.id!,
              firstName: input.firstName,
              lastName: input.lastName,
              phoneNumber: input.phone ?? undefined,
              workerResourceId: input.resourceId,
            },
          });

          return {
            id: result.account?.id ?? input.id!,
            email: result.account?.email ?? input.email,
            name: result.account?.name ?? '',
          };
        },
        { cachePolicy: 'no-cache' }
      );
    } else {
      return resolve(
        ({ mutation }) => {
          const result = mutation.inviteWorker({
            input: {
              email: input.email,
              firstName: input.firstName,
              lastName: input.lastName,
              phoneNumber: input.phone ?? undefined,
              workerResourceId: input.resourceId,
            },
          });

          return {
            id: result.account?.id ?? '',
            email: result.account?.email ?? input.email,
            name: result.account?.name ?? '',
          };
        },
        { cachePolicy: 'no-cache' }
      );
    }
  }

  /**
   * Get worker resources with cursor-based pagination
   */
  async getWorkerResourcesCursor(params: { cursor: string | null; search?: string | null }) {
    const { cursor, search } = params;
    const where: any = {};

    if (search) {
      where.name = { contains: search };
    }

    return resolve(
      ({ query }) => {
        const conn = query.workerResources({
          ...forwardPagination(25, cursor),
          where: Object.keys(where).length > 0 ? where : undefined,
        });

        if (!conn) return { nodes: [], pageInfo: { endCursor: null, hasNextPage: false } };

        const nodes = conn.nodes?.map((node) => {
          // Explicitly access worker fields to trigger gqty to fetch them
          const worker = node.worker;
          const workerEmail = worker?.email;
          const workerFirstName = worker?.firstName;
          const workerLastName = worker?.lastName;
          
          const allocatedTo = worker 
            ? (`${workerFirstName ?? ''} ${workerLastName ?? ''}`.trim() || workerEmail) ?? 'Unknown'
            : undefined;
          
          return {
            id: node.id,
            code: node.code ?? '',
            isAllocated: !!worker,
            allocatedTo,
          };
        }) ?? [];

        return {
          nodes,
          pageInfo: {
            endCursor: conn.pageInfo?.endCursor,
            hasNextPage: conn.pageInfo?.hasNextPage,
          },
        };
      },
      { cachePolicy: 'no-cache' }
    );
  }

  /**
   * Delete a worker
   */
  async deleteWorker(id: string) {
    return resolve(
      ({ mutation }) => {
        const result = mutation.deleteAccount({ input: { id } });
        void result.errors;
        return true;
      },
      { cachePolicy: 'no-cache' }
    );
  }

  /**
   * Get a single manager by ID
   * Note: Managers are users with MANAGER role in the system
   */
  async getManager(id: string): Promise<Manager | null> {
    return resolve(
      ({ query }) => {
        const manager = query.manager({ id });
        if (!manager) return null;

        return {
          id: manager.id,
          email: manager.email ?? '',
          firstName: manager.firstName ?? '',
          lastName: manager.lastName ?? '',
          name: manager.name ?? '',
          status: manager.status,
          createdAt: manager.createdAt,
        };
      },
      { cachePolicy: 'no-cache' }
    );
  }

  /**
   * Get managers with cursor-based pagination
   * Note: Managers are users with MANAGER role
   */
  async getManagersCursor(params: { cursor: string | null; search?: string | null }) {
    const { cursor, search } = params;
    const where: any = {};

    if (search) {
      where.search = { contains: search };
    }

    return resolve(
      ({ query }) => {
        const conn = query.managers({
          ...forwardPagination(25, cursor),
          where: Object.keys(where).length > 0 ? where : undefined,
        });

        if (!conn) return { nodes: [], pageInfo: { endCursor: null, hasNextPage: false } };

        const nodes = conn.nodes?.map((node) => ({
          id: node.id,
          email: node.email ?? '',
          firstName: node.firstName ?? '',
          lastName: node.lastName ?? '',
          name: node.name ?? '',
          status: node.status,
          createdAt: node.createdAt,
        })) ?? [];

        return {
          nodes,
          pageInfo: {
            endCursor: conn.pageInfo?.endCursor,
            hasNextPage: conn.pageInfo?.hasNextPage,
          },
        };
      },
      { cachePolicy: 'no-cache' }
    );
  }

  /**
   * Create or update a manager
   */
  async upsertManager(input: { id?: string; email: string; firstName: string; lastName: string }) {
    if (input.id) {
      return resolve(
        ({ mutation }) => {
          const result = mutation.updateAccount({
            input: {
              id: input.id!,
              firstName: input.firstName,
              lastName: input.lastName,
            },
          });

          return {
            id: input.id!,
            email: input.email,
            name: result.account?.name ?? '',
          };
        },
        { cachePolicy: 'no-cache' }
      );
    } else {
      return resolve(
        ({ mutation }) => {
          const result = mutation.inviteOperator({
            input: {
              email: input.email,
              firstName: input.firstName,
              lastName: input.lastName,
            },
          });

          return {
            id: result.account?.id ?? '',
            email: result.account?.email ?? input.email,
            name: result.account?.name ?? '',
          };
        },
        { cachePolicy: 'no-cache' }
      );
    }
  }

  /**
   * Delete a manager
   */
  async deleteManager(id: string) {
    return resolve(
      ({ mutation }) => {
        const result = mutation.deleteAccount({ input: { id } });
        void result.errors;
        return true;
      },
      { cachePolicy: 'no-cache' }
    );
  }

  /**
   * Get a single administrator by ID
   */
  async getAdministrator(id: string): Promise<Administrator | null> {
    return resolve(
      ({ query }) => {
        const admin = query.admin({ id });
        if (!admin) return null;

        return {
          id: admin.id,
          email: admin.email ?? '',
          firstName: admin.firstName ?? '',
          lastName: admin.lastName ?? '',
          name: admin.name ?? '',
          status: admin.status,
          createdAt: admin.createdAt,
        };
      },
      { cachePolicy: 'no-cache' }
    );
  }

  /**
   * Get administrators with cursor-based pagination
   */
  async getAdministratorsCursor(params: { cursor: string | null; search?: string | null }) {
    const { cursor, search } = params;
    const where: any = {};

    if (search) {
      where.search = { contains: search };
    }

    return resolve(
      ({ query }) => {
        const conn = query.admins({
          ...forwardPagination(25, cursor),
          where: Object.keys(where).length > 0 ? where : undefined,
        });

        if (!conn) return { nodes: [], pageInfo: { endCursor: null, hasNextPage: false } };

        const nodes = conn.nodes?.map((node) => ({
          id: node.id,
          email: node.email ?? '',
          firstName: node.firstName ?? '',
          lastName: node.lastName ?? '',
          name: node.name ?? '',
          status: node.status,
          createdAt: node.createdAt,
        })) ?? [];

        return {
          nodes,
          pageInfo: {
            endCursor: conn.pageInfo?.endCursor,
            hasNextPage: conn.pageInfo?.hasNextPage,
          },
        };
      },
      { cachePolicy: 'no-cache' }
    );
  }

  /**
   * Create or update an administrator
   */
  async upsertAdministrator(input: { id?: string; email: string; firstName: string; lastName: string }) {
    if (input.id) {
      return resolve(
        ({ mutation }) => {
          const result = mutation.updateAccount({
            input: {
              id: input.id!,
              firstName: input.firstName,
              lastName: input.lastName,
            },
          });

          return {
            id: input.id!,
            email: input.email,
            name: result.account?.name ?? '',
          };
        },
        { cachePolicy: 'no-cache' }
      );
    } else {
      return resolve(
        ({ mutation }) => {
          const result = mutation.inviteAdministrator({
            input: {
              email: input.email,
              firstName: input.firstName,
              lastName: input.lastName,
            },
          });

          return {
            id: result.account?.id ?? '',
            email: result.account?.email ?? input.email,
            name: result.account?.name ?? '',
          };
        },
        { cachePolicy: 'no-cache' }
      );
    }
  }

  /**
   * Delete an administrator
   */
  async deleteAdministrator(id: string) {
    // Decode inside the resolver (so gqty selects the error fields), then throw
    // AFTER it settles — throwing inside would abort the mutation before any
    // request is sent.
    const info = await resolve(
      ({ mutation }) => {
        const result = mutation.deleteAccount({ input: { id } });
        return readDeleteAccountError(result.errors);
      },
      { cachePolicy: 'no-cache' }
    );
    throwIfDeleteBlocked(info);
    return true;
  }

  /** Forces a password reset for an account (the server emails a reset link). */
  async forcePasswordReset(accountId: string): Promise<void> {
    return resolve(
      ({ mutation }) => {
        const payload = mutation.forcePasswordReset({ input: { accountId } });
        void payload.account?.id;
        touchMutationErrors(payload.errors);
        if (payload.errors?.length) {
          throw new Error(formatApiError({ errors: payload.errors }));
        }
      },
      { cachePolicy: 'no-cache' },
    );
  }

  /** Re-sends the invitation email for a still-pending account. */
  async resendAccountInvitation(accountId: string): Promise<void> {
    return resolve(
      ({ mutation }) => {
        const payload = mutation.resendAccountInvitation({ input: { accountId } });
        void payload.account?.id;
        touchMutationErrors(payload.errors);
        if (payload.errors?.length) {
          throw new Error(formatApiError({ errors: payload.errors }));
        }
      },
      { cachePolicy: 'no-cache' },
    );
  }

  /**
   * Get a single webhook by ID (MOCK)
   */
  async getWebhook(id: string): Promise<Webhook | null> {
    // Mock implementation - simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 100));
    return this.mockWebhooks.find((w) => w.id === id) || null;
  }

  /**
   * Get webhooks with cursor-based pagination (MOCK)
   */
  async getWebhooksCursor(params: { cursor: string | null; search?: string | null }) {
    // Mock implementation - simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 150));

    let filtered = [...this.mockWebhooks];

    // Apply search filter
    if (params.search) {
      const searchLower = params.search.toLowerCase();
      filtered = filtered.filter(
        (w) =>
          w.name.toLowerCase().includes(searchLower) ||
          w.url.toLowerCase().includes(searchLower)
      );
    }

    // Sort by createdAt descending
    filtered.sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return dateB - dateA;
    });

    return {
      nodes: filtered,
      pageInfo: { endCursor: null, hasNextPage: false },
    };
  }

  /**
   * Create or update a webhook (MOCK)
   */
  async upsertWebhook(input: {
    id?: string;
    name: string;
    url: string;
    events: string[];
    isActive: boolean;
  }) {
    // Mock implementation - simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 200));

    if (input.id) {
      // Update existing
      const index = this.mockWebhooks.findIndex((w) => w.id === input.id);
      if (index !== -1) {
        this.mockWebhooks[index] = {
          ...this.mockWebhooks[index],
          name: input.name,
          url: input.url,
          events: input.events,
          isActive: input.isActive,
        };
        return this.mockWebhooks[index];
      }
      throw new Error('Webhook not found');
    } else {
      // Create new
      const newWebhook: Webhook = {
        id: String(this.webhookIdCounter++),
        name: input.name,
        url: input.url,
        events: input.events,
        isActive: input.isActive,
        secret: this.generateMockSecret(),
        createdAt: new Date(),
      };
      this.mockWebhooks.push(newWebhook);
      return newWebhook;
    }
  }

  /**
   * Delete a webhook (MOCK)
   */
  async deleteWebhook(id: string) {
    // Mock implementation - simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 150));

    const index = this.mockWebhooks.findIndex((w) => w.id === id);
    if (index !== -1) {
      this.mockWebhooks.splice(index, 1);
      return true;
    }
    return false;
  }

  /**
   * Test webhook URL (MOCK)
   */
  async testWebhook(url: string): Promise<{ success: boolean; message: string }> {
    // Mock implementation - simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 500));

    // Simple URL validation
    try {
      new URL(url);
      // Mock: consider URLs with 'example.com' as test URLs that succeed
      const isTestUrl = url.includes('example.com') || url.includes('localhost');
      return {
        success: isTestUrl,
        message: isTestUrl
          ? 'Test payload delivered successfully'
          : 'Could not reach endpoint (mock)',
      };
    } catch {
      return { success: false, message: 'Invalid URL format' };
    }
  }

  /**
   * Get available webhook event types (MOCK)
   */
  getAvailableWebhookEvents(): string[] {
    return [
      'workPackage.created',
      'workPackage.updated',
      'workPackage.deleted',
      'workPackage.statusChanged',
      'task.created',
      'task.updated',
      'task.deleted',
      'task.assigned',
      'task.completed',
      'worker.created',
      'worker.updated',
      'worker.deleted',
      'planning.scheduled',
      'planning.rescheduled',
      'resource.assigned',
      'resource.released',
    ];
  }

  private generateMockSecret(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let secret = '';
    for (let i = 0; i < 32; i++) {
      secret += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return secret;
  }

  /**
   * Get a single API key by ID
   */
  async getApiKey(id: string): Promise<ApiKey | null> {
    return resolve(
      ({ query }) => {
        const apiKey = query.apiKey({ id });
        if (!apiKey) return null;

        return {
          id: apiKey.id,
          name: apiKey.name ?? '',
        };
      },
      { cachePolicy: 'no-cache' }
    );
  }

  /**
   * Get API keys with cursor-based pagination
   */
  async getApiKeysCursor(params: { cursor: string | null; search?: string | null }) {
    const { cursor, search } = params;
    const where: any = {};

    if (search) {
      where.name = { contains: search };
    }

    return resolve(
      ({ query }) => {
        const conn = query.apiKeys({
          ...forwardPagination(25, cursor),
          where: Object.keys(where).length > 0 ? where : undefined,
        });

        if (!conn) return { nodes: [], pageInfo: { endCursor: null, hasNextPage: false } };

        const nodes = conn.nodes?.map((node) => ({
          id: node.id,
          name: node.name ?? '',
        })) ?? [];

        return {
          nodes,
          pageInfo: {
            endCursor: conn.pageInfo?.endCursor,
            hasNextPage: conn.pageInfo?.hasNextPage,
          },
        };
      },
      { cachePolicy: 'no-cache' }
    );
  }

  /**
   * Create an API key
   */
  async upsertApiKey(input: { name: string; roles: string[] }) {
    return resolve(
      ({ mutation }) => {
        const result = mutation.createApiKey({
          input: {
            name: input.name,
            roles: input.roles,
          },
        });

        return {
          id: result.apiKey?.id ?? '',
          name: result.apiKey?.name ?? input.name,
          key: result.apiKey?.key ?? undefined,
        };
      },
      { cachePolicy: 'no-cache' }
    );
  }

  /**
   * Delete an API key
   */
  async deleteApiKey(id: string) {
    return resolve(
      ({ mutation }) => {
        const result = mutation.deleteApiKey({ input: { id } });
        void result.errors;
        return true;
      },
      { cachePolicy: 'no-cache' }
    );
  }

  /**
   * Get work package types with cursor-based pagination
   * @TODO: Implement when wpTypes query is available in GraphQL
   */
  async getWpTypesCursor(params: { cursor: string | null; search?: string | null }) {
    // Placeholder implementation
    return {
      nodes: [],
      pageInfo: { endCursor: null, hasNextPage: false },
    };
  }

  /**
   * Create or update a work package type
   * @TODO: Implement when wpTypes mutation is available in GraphQL
   */
  async upsertWpType(input: any) {
    // Placeholder implementation
    return { id: 'placeholder', name: 'Placeholder WP Type' };
  }

  /**
   * Delete a work package type
   * @TODO: Implement when wpTypes mutation is available in GraphQL
   */
  async deleteWpType(id: string) {
    // Placeholder implementation
    return true;
  }

  // ============================================
  // Settings Entities (General, Licenses, etc.)
  // ============================================

  async getGeneralSettings(): Promise<GeneralSettings> {
    const row = await resolve(
      ({ query }) => {
        const item = query.tenantSettings;
        if (!item) return null;
        return {
          id: item.id,
          tenantName: item.tenantName,
          tenantCode: item.tenantCode,
          timezone: item.timezone,
          defaultCurrency: item.defaultCurrency,
          language: item.language,
        };
      },
      { cachePolicy: 'no-cache' }
    );

    if (!row) {
      return {
        tenantName: '',
        tenantCode: '',
        timezone: 'UTC',
        defaultCurrency: 'EUR',
        language: TenantLanguage.IT,
      };
    }

    return {
      tenantName: row.tenantName,
      tenantCode: row.tenantCode,
      timezone: row.timezone,
      defaultCurrency: row.defaultCurrency,
      language: row.language,
    };
  }

  async updateGeneralSettings(settings: GeneralSettings): Promise<GeneralSettings> {
    const existing = await resolve(
      ({ query }) => query.tenantSettings?.id ?? null,
      { cachePolicy: 'no-cache' }
    );

    if (!existing) {
      throw new Error('Tenant settings not found');
    }

    const res = await resolve(
      ({ mutation }) => {
        const r = mutation.updateTenantSettings({
          input: {
            id: existing,
            tenantName: settings.tenantName,
            tenantCode: settings.tenantCode,
            timezone: settings.timezone,
            defaultCurrency: settings.defaultCurrency,
            language: settings.language,
          },
        });
        const updated = r.tenantSettings;
        return {
          tenantName: updated?.tenantName ?? settings.tenantName,
          tenantCode: updated?.tenantCode ?? settings.tenantCode,
          timezone: updated?.timezone ?? settings.timezone,
          defaultCurrency: updated?.defaultCurrency ?? settings.defaultCurrency,
          language: updated?.language ?? settings.language,
          error: settingsErrorMessage(r.errors?.[0]),
        };
      },
      { cachePolicy: 'no-cache' }
    );

    if (res.error) throw new Error(`updateTenantSettings: ${res.error}`);
    return {
      tenantName: res.tenantName,
      tenantCode: res.tenantCode,
      timezone: res.timezone,
      defaultCurrency: res.defaultCurrency,
      language: res.language,
    };
  }

  /**
   * Get this tenant's licenses. Licenses are managed by Everplan and pushed in via sync or
   * signed-file import; the tenant can only view them.
   */
  async getLicenses(): Promise<License[]> {
    return resolve(
      ({ query }) => {
        const licenses = query.licenses({}) ?? [];
        return licenses.map((l) => ({
          id: l.id,
          type: l.type,
          qt: l.qt,
          validFrom: l.validFrom,
          validTo: l.validTo,
          isTrial: l.isTrial,
          singlePrice: Number(l.singlePrice),
          isActive: l.isActive,
          seatsInUse: l.seatsInUse,
        }));
      },
      { cachePolicy: 'no-cache' }
    );
  }

  /**
   * Imports an Everplan-issued, signed license envelope (the air-gapped/on-prem
   * delivery path). Returns how many licenses were imported and when.
   */
  async importLicenses(
    envelopeJson: string,
  ): Promise<{ importedCount: number; importedAt: string }> {
    return resolve(
      ({ mutation }) => {
        const payload = mutation.importLicenses({ input: { envelopeJson } });
        const result = payload.importLicensesResult;
        const importedCount = result?.importedCount ?? 0;
        const importedAt = result?.importedAt ? String(result.importedAt) : '';
        touchMutationErrors(payload.errors);
        if (payload.errors?.length) {
          throw new Error(formatApiError({ errors: payload.errors }));
        }
        if (!result) {
          // No errors and no result is an unexpected response — don't report success.
          throw new Error('License import returned no result.');
        }
        return { importedCount, importedAt };
      },
      { cachePolicy: 'no-cache' },
    );
  }

  /**
   * Seat usage for a single module type, aggregated across that module's active licenses.
   * Total is the summed quota of active licenses (matching backend seat enforcement); used is the
   * count of consumed seats. Returns null when the tenant has no active license of that type.
   */
  async getSeatUsage(type: SeatLicenseType): Promise<SeatUsage | null> {
    const licenses = await this.getLicenses();
    const active = licenses.filter((l) => l.type === type && l.isActive);
    if (active.length === 0) return null;

    const total = active.reduce((sum, l) => sum + l.qt, 0);
    const used = active.reduce((sum, l) => sum + l.seatsInUse, 0);
    return { type, used, total, isFull: used >= total };
  }

  async getPlanningSettings(): Promise<{
    planningId: string | null;
    executionId: string | null;
    /** Whether a Google key is stored. The key itself is never returned. */
    hasGoogleMapsApiKey: boolean;
    settings: PlanningSettingsForm;
  }> {
    const data = await resolve(
      ({ query }) => {
        const planning = query.planningSettings;
        const execution = query.executionSettings;
        if (!planning) return null;
        return {
          planningId: planning.id,
          executionId: execution?.id ?? null,
          defaultSolverType: planning.defaultSolverType,
          defaultPlanningHorizonDays: planning.defaultPlanningHorizonDays,
          penaltyAwarePriority: planning.penaltyAwarePriority,
          defaultMaxOvertimeMinutes: planning.defaultMaxOvertimeMinutes,
          defaultShiftEndBehaviour: (planning.defaultShiftEndBehaviour ?? 'RETURN_TO_DEPOT') as 'RETURN_TO_DEPOT' | 'STAY_IN_PLACE',
          defaultTimeLimitSec: planning.defaultTimeLimitSec,
          defaultPlanningViewMode: planning.defaultPlanningViewMode,
          defaultRoutingProvider: planning.defaultRoutingProvider,
          hasGoogleMapsApiKey: planning.hasGoogleMapsApiKey,
          autoScheduleEnabled: planning.autoScheduleEnabled,
          allowOverlappingTasks: planning.allowOverlappingTasks,
          workingHoursStart: planning.workingHoursStart as string | null,
          workingHoursEnd: planning.workingHoursEnd as string | null,
          stepTitle: planning.stepTitle,
          stepSubtitle: planning.stepSubtitle,
          stepShowCode: planning.stepShowCode,
          stepShowTaskBadge: planning.stepShowTaskBadge,
          requireApplyConfirmation: planning.requireApplyConfirmation,
          workerPushLeadMinutes: planning.workerPushLeadMinutes,
          averageTravelSpeedKmh: planning.averageTravelSpeedKmh,
          defaultCloseStopTasksPolicy: execution?.defaultCloseStopTasksPolicy ?? 'COMPLETE_ALL_OPEN',
          defaultStartNextStopsPolicy: execution?.defaultStartNextStopsPolicy ?? 'ALWAYS',
          autoCloseStopTasksPolicy: execution?.autoCloseStopTasksPolicy ?? 'COMPLETE_LINKED_TASKS',
          startTasksOnStepStarted: execution?.startTasksOnStepStarted ?? true,
          inProgressWorkPackageOnTaskStart: execution?.inProgressWorkPackageOnTaskStart ?? true,
          inProgressWorkShiftOnStepStart: execution?.inProgressWorkShiftOnStepStart ?? true,
          autoStartLaneDelayedStepOnWorkShiftStart:
            execution?.autoStartLaneDelayedStepOnWorkShiftStart ?? true,
          completeWorkPackageOnAllTaskComplete: execution?.completeWorkPackageOnAllTaskComplete ?? true,
          completeWorkShiftOnAllStepComplete: execution?.completeWorkShiftOnAllStepComplete ?? true,
        };
      },
      { cachePolicy: 'no-cache' }
    );

    if (!data?.planningId) {
      throw new Error('Planning settings not found');
    }

    return {
      planningId: data.planningId,
      executionId: data.executionId,
      hasGoogleMapsApiKey: data.hasGoogleMapsApiKey,
      settings: {
        defaultSolverType: data.defaultSolverType,
        defaultPlanningHorizonDays: data.defaultPlanningHorizonDays,
        penaltyAwarePriority: data.penaltyAwarePriority,
        defaultMaxOvertimeMinutes: data.defaultMaxOvertimeMinutes,
        defaultShiftEndBehaviour: data.defaultShiftEndBehaviour,
        defaultTimeLimitSec: data.defaultTimeLimitSec,
        defaultPlanningViewMode: data.defaultPlanningViewMode,
        defaultRoutingProvider: data.defaultRoutingProvider,
        defaultCloseStopTasksPolicy: data.defaultCloseStopTasksPolicy,
        defaultStartNextStopsPolicy: data.defaultStartNextStopsPolicy,
        autoCloseStopTasksPolicy: data.autoCloseStopTasksPolicy,
        startTasksOnStepStarted: data.startTasksOnStepStarted,
        inProgressWorkPackageOnTaskStart: data.inProgressWorkPackageOnTaskStart,
        inProgressWorkShiftOnStepStart: data.inProgressWorkShiftOnStepStart,
        autoStartLaneDelayedStepOnWorkShiftStart: data.autoStartLaneDelayedStepOnWorkShiftStart,
        completeWorkPackageOnAllTaskComplete: data.completeWorkPackageOnAllTaskComplete,
        completeWorkShiftOnAllStepComplete: data.completeWorkShiftOnAllStepComplete,
        autoScheduleEnabled: data.autoScheduleEnabled,
        allowOverlappingTasks: data.allowOverlappingTasks,
        workingHoursStart: localTimeToInput(data.workingHoursStart),
        workingHoursEnd: localTimeToInput(data.workingHoursEnd),
        stepTitle: data.stepTitle,
        stepSubtitle: data.stepSubtitle,
        stepShowCode: data.stepShowCode,
        stepShowTaskBadge: data.stepShowTaskBadge,
        requireApplyConfirmation: data.requireApplyConfirmation,
        workerPushLeadMinutes: data.workerPushLeadMinutes,
        averageTravelSpeedKmh: data.averageTravelSpeedKmh,
        googleMapsApiKey: '',
        clearGoogleMapsApiKey: false,
      },
    };
  }

  /**
   * Lightweight read of the single flag that gates the Autoplan wizard's apply
   * confirmation (avoids pulling the full {@link getPlanningSettings} form just
   * to decide whether to show the review step). Defaults to `true` (confirm
   * required) if settings can't be read, matching the story's recommended
   * fail-safe fallback.
   */
  async getRequireApplyConfirmation(): Promise<boolean> {
    try {
      const value = await resolve(
        ({ query }) => query.planningSettings?.requireApplyConfirmation ?? null,
        { cachePolicy: 'no-cache' }
      );
      return value ?? true;
    } catch {
      return true;
    }
  }

  async updatePlanningSettings(settings: PlanningSettingsForm): Promise<PlanningSettingsForm> {
    const ids = await resolve(
      ({ query }) => {
        const planning = query.planningSettings;
        const execution = query.executionSettings;
        return {
          planningId: planning?.id ?? null,
          executionId: execution?.id ?? null,
        };
      },
      { cachePolicy: 'no-cache' }
    );

    if (!ids.planningId) {
      throw new Error('Planning settings not found');
    }

    const workingHoursStart = inputToLocalTime(settings.workingHoursStart);
    const workingHoursEnd = inputToLocalTime(settings.workingHoursEnd);

    // Omitted entirely when untouched: the backend leaves the stored key alone,
    // which is the only way to keep a value the UI can never read back.
    const googleMapsApiKey = settings.clearGoogleMapsApiKey
      ? { googleMapsApiKey: null }
      : settings.googleMapsApiKey.trim()
        ? { googleMapsApiKey: settings.googleMapsApiKey.trim() }
        : {};

    const planningRes = await resolve(
      ({ mutation }) => {
        const r = mutation.updatePlanningSettings({
          input: {
            id: ids.planningId!,
            defaultSolverType: settings.defaultSolverType,
            defaultPlanningHorizonDays: settings.defaultPlanningHorizonDays,
            penaltyAwarePriority: settings.penaltyAwarePriority,
            defaultMaxOvertimeMinutes: settings.defaultMaxOvertimeMinutes,
            defaultShiftEndBehaviour: settings.defaultShiftEndBehaviour as never,
            defaultTimeLimitSec: settings.defaultTimeLimitSec,
            defaultPlanningViewMode: settings.defaultPlanningViewMode,
            defaultRoutingProvider: settings.defaultRoutingProvider,
            autoScheduleEnabled: settings.autoScheduleEnabled,
            allowOverlappingTasks: settings.allowOverlappingTasks,
            stepTitle: settings.stepTitle,
            stepSubtitle: settings.stepSubtitle,
            stepShowCode: settings.stepShowCode,
            stepShowTaskBadge: settings.stepShowTaskBadge,
            requireApplyConfirmation: settings.requireApplyConfirmation,
            workerPushLeadMinutes: settings.workerPushLeadMinutes,
            averageTravelSpeedKmh: settings.averageTravelSpeedKmh,
            ...(workingHoursStart != null ? { workingHoursStart } : {}),
            ...(workingHoursEnd != null ? { workingHoursEnd } : {}),
            ...googleMapsApiKey,
          },
        });
        const err = r.errors?.[0];
        const updated = r.planningSettings;
        return {
          defaultSolverType: updated?.defaultSolverType ?? settings.defaultSolverType,
          error: settingsErrorMessage(err),
        };
      },
      { cachePolicy: 'no-cache' }
    );

    if (planningRes.error) {
      throw new Error(planningRes.error);
    }

    if (ids.executionId) {
      const executionRes = await resolve(
        ({ mutation }) => {
          const r = mutation.updateExecutionSettings({
            input: {
              id: ids.executionId!,
              defaultCloseStopTasksPolicy: settings.defaultCloseStopTasksPolicy as CloseStepTasksPolicy,
              defaultStartNextStopsPolicy: settings.defaultStartNextStopsPolicy as StartNextStepPolicy,
              autoCloseStopTasksPolicy: settings.autoCloseStopTasksPolicy as AutoCloseStepTasksPolicy,
              startTasksOnStepStarted: settings.startTasksOnStepStarted,
              inProgressWorkPackageOnTaskStart: settings.inProgressWorkPackageOnTaskStart,
              inProgressWorkShiftOnStepStart: settings.inProgressWorkShiftOnStepStart,
              autoStartLaneDelayedStepOnWorkShiftStart: settings.autoStartLaneDelayedStepOnWorkShiftStart,
              completeWorkPackageOnAllTaskComplete: settings.completeWorkPackageOnAllTaskComplete,
              completeWorkShiftOnAllStepComplete: settings.completeWorkShiftOnAllStepComplete,
            },
          });
          const err = r.errors?.[0];
          const updated = r.executionSettings;
          return {
            id: updated?.id ?? ids.executionId,
            error: settingsErrorMessage(err),
          };
        },
        { cachePolicy: 'no-cache' }
      );

      if (executionRes.error) {
        throw new Error(executionRes.error);
      }
    }

    return settings;
  }

  // ============================================
  // Work Package Types
  // ============================================

  /**
   * Get a single work package type by ID
   */
  async getWorkPackageType(id: string): Promise<WorkPackageType | null> {
    return resolve(
      ({ query }) => {
        const wpType = query.workPackageType({ id });
        if (!wpType) return null;

        return {
          id: wpType.id,
          name: wpType.name,
          createdAt: wpType.createdAt,
        };
      },
      { cachePolicy: 'no-cache' }
    );
  }

  /**
   * Get work package types with cursor-based pagination
   */
  async getWorkPackageTypesCursor(params: { cursor: string | null; search?: string | null }) {
    const { cursor, search } = params;
    const where: any = {};

    if (search) {
      where.search = { contains: search };
    }

    return resolve(
      ({ query }) => {
        const conn = query.workPackageTypes({
          ...forwardPagination(25, cursor),
          where: Object.keys(where).length > 0 ? where : undefined,
        });

        if (!conn) return { nodes: [], pageInfo: { endCursor: null, hasNextPage: false } };

        const nodes = conn.nodes?.map((node) => ({
          id: node.id,
          name: node.name,
          createdAt: node.createdAt,
        })) ?? [];

        return {
          nodes,
          pageInfo: {
            endCursor: conn.pageInfo?.endCursor,
            hasNextPage: conn.pageInfo?.hasNextPage,
          },
        };
      },
      { cachePolicy: 'no-cache' }
    );
  }

  /**
   * Create or update a work package type
   */
  async upsertWorkPackageType(input: { id?: string; name: string }) {
    if (input.id) {
      return resolve(
        ({ mutation }) => {
          const result = mutation.updateWorkPackageType({
            input: {
              id: input.id!,
              name: input.name,
            },
          });

          return {
            id: result.workPackageType?.id ?? input.id!,
            name: result.workPackageType?.name ?? input.name,
          };
        },
        { cachePolicy: 'no-cache' }
      );
    } else {
      return resolve(
        ({ mutation }) => {
          const result = mutation.createWorkPackageType({
            input: {
              name: input.name,
            },
          });

          return {
            id: result.workPackageType?.id ?? '',
            name: result.workPackageType?.name ?? input.name,
          };
        },
        { cachePolicy: 'no-cache' }
      );
    }
  }

  /**
   * Delete a work package type
   */
  async deleteWorkPackageType(id: string) {
    return resolve(
      ({ mutation }) => {
        mutation.deleteWorkPackageType({ input: { id } });
        return true;
      },
      { cachePolicy: 'no-cache' }
    );
  }

  // ============================================
  // Resource Features
  // ============================================

  /**
   * Get a single resource feature by ID
   */
  async getResourceFeature(id: string): Promise<ResourceFeature | null> {
    return resolve(
      ({ query }) => {
        const feature = query.resourceFeature({ id });
        if (!feature) return null;

        return {
          id: feature.id,
          name: feature.name,
          createdAt: feature.createdAt,
        };
      },
      { cachePolicy: 'no-cache' }
    );
  }

  /**
   * Get resource features with cursor-based pagination
   */
  async getResourceFeaturesCursor(params: { cursor: string | null; search?: string | null }) {
    const { cursor, search } = params;
    const where: any = {};

    if (search) {
      where.search = { contains: search };
    }

    return resolve(
      ({ query }) => {
        const conn = query.resourceFeatures({
          ...forwardPagination(25, cursor),
          where: Object.keys(where).length > 0 ? where : undefined,
        });

        if (!conn) return { nodes: [], pageInfo: { endCursor: null, hasNextPage: false } };

        const nodes = conn.nodes?.map((node) => ({
          id: node.id,
          name: node.name,
          createdAt: node.createdAt,
        })) ?? [];

        return {
          nodes,
          pageInfo: {
            endCursor: conn.pageInfo?.endCursor,
            hasNextPage: conn.pageInfo?.hasNextPage,
          },
        };
      },
      { cachePolicy: 'no-cache' }
    );
  }

  /**
   * Create or update a resource feature
   */
  async upsertResourceFeature(input: { id?: string; name: string }) {
    if (input.id) {
      return resolve(
        ({ mutation }) => {
          const result = mutation.updateResourceFeature({
            input: {
              id: input.id!,
              name: input.name,
            },
          });

          return {
            id: result.resourceFeature?.id ?? input.id!,
            name: result.resourceFeature?.name ?? input.name,
          };
        },
        { cachePolicy: 'no-cache' }
      );
    } else {
      return resolve(
        ({ mutation }) => {
          const result = mutation.createResourceFeature({
            input: {
              name: input.name,
            },
          });

          return {
            id: result.resourceFeature?.id ?? '',
            name: result.resourceFeature?.name ?? input.name,
          };
        },
        { cachePolicy: 'no-cache' }
      );
    }
  }

  /**
   * Delete a resource feature
   */
  async deleteResourceFeature(id: string) {
    return resolve(
      ({ mutation }) => {
        mutation.deleteResourceFeature({ input: { id } });
        return true;
      },
      { cachePolicy: 'no-cache' }
    );
  }

  // ============================================
  // Work Package Templates
  // ============================================

  /**
   * Get a single work package template by ID
   */
  async getWorkPackageTemplate(id: string): Promise<WorkPackageTemplate | null> {
    return resolve(
      ({ query }) => {
        const template = query.workPackageTemplate({ id });
        if (!template) return null;

        return {
          id: template.id,
          code: template.code ?? null,
          name: template.name ?? null,
          notes: template.notes ?? null,
          createdAt: template.createdAt,
        };
      },
      { cachePolicy: 'no-cache' }
    );
  }

  /**
   * Get work package templates with cursor-based pagination
   */
  async getWorkPackageTemplatesCursor(params: { cursor: string | null; search?: string | null }) {
    const { cursor, search } = params;
    const where: any = {};

    if (search) {
      where.search = { contains: search };
    }

    return resolve(
      ({ query }) => {
        const conn = query.workPackageTemplates({
          ...forwardPagination(25, cursor),
          where: Object.keys(where).length > 0 ? where : undefined,
        });

        if (!conn) return { nodes: [], pageInfo: { endCursor: null, hasNextPage: false } };

        const nodes = conn.nodes?.map((node) => ({
          id: node.id,
          code: node.code ?? null,
          name: node.name ?? null,
          notes: node.notes ?? null,
          createdAt: node.createdAt,
        })) ?? [];

        return {
          nodes,
          pageInfo: {
            endCursor: conn.pageInfo?.endCursor,
            hasNextPage: conn.pageInfo?.hasNextPage,
          },
        };
      },
      { cachePolicy: 'no-cache' }
    );
  }

  /**
   * Rename a work package template (only name update)
   * Note: Templates are created from workPackageUpsertDialog, not here
   */
  async renameWorkPackageTemplate(input: { id: string; name: string }) {
    return resolve(
      ({ mutation }) => {
        const result = mutation.updateWorkPackageTemplate({
          input: {
            id: input.id,
            name: input.name,
          },
        });

        return {
          id: result.workPackageTemplate?.id ?? input.id,
          name: result.workPackageTemplate?.name ?? input.name,
        };
      },
      { cachePolicy: 'no-cache' }
    );
  }

  /**
   * Delete a work package template
   */
  async deleteWorkPackageTemplate(id: string) {
    return resolve(
      ({ mutation }) => {
        mutation.deleteWorkPackageTemplate({ input: { id } });
        return true;
      },
      { cachePolicy: 'no-cache' }
    );
  }
}
